import { useEffect, useState } from 'react';
import { api } from '@/lib/api/client';

export type UsernameStatus = 'idle' | 'checking' | 'available' | 'unavailable';

interface Props {
  username: string;
  excludeUsername?: string;
  onStatusChange?: (s: UsernameStatus) => void;
}

export function UsernameAvailability({ username, excludeUsername = '', onStatusChange }: Props) {
  const [status, setStatus] = useState<UsernameStatus>('idle');

  useEffect(() => {
    onStatusChange?.(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    const trimmed = (username ?? '').trim();
    if (!trimmed || trimmed === excludeUsername) {
      setStatus('idle');
      return;
    }
    setStatus('checking');
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get<{ available: boolean }>(
          `/api/obd/username/available/${encodeURIComponent(trimmed)}`,
        );
        if (!cancelled) setStatus(res?.available ? 'available' : 'unavailable');
      } catch {
        if (!cancelled) setStatus('idle');
      }
    }, 500); // 500ms debounce, same as old
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [username, excludeUsername]);

  if (status === 'idle' || status === 'checking') return null;
  return (
    <p className={status === 'available' ? 'text-sm text-green-600' : 'text-sm text-destructive'}>
      '{username.trim()}' is {status === 'available' ? 'available' : 'not available'}.
    </p>
  );
}